import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import httpService from "../services/httpService";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from "react-bootstrap/Badge";
import Card from 'react-bootstrap/Card';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";

import {
  FaPlay, FaPause,
  FaExternalLinkAlt, FaStepBackward,
  FaRegLightbulb, FaComments, FaCommentAlt,
  FaUsers,
} from "react-icons/fa";

import "../styles/dashbaords.scss";
import "../styles/ListenerEpisode.scss";
import LoadingFade from "../components/LoadingFade";
import KeyTakeawayImageWithPlaceholder from "../components/KeyTakeawayImageWithPlaceholder";
import PracticeModal from "../components/ListenerComponents/PracticeModal";
import ListenerEpisodePublicPractices from "../components/ListenerComponents/ListenerEpisodePublicPractices";
import { getImage } from "../utils/podcastClubUtils";
import { useAudioPlayerStore } from "../store/store";

/* ─── Helpers ─────────────────────────────────────── */
const getInitials = (user) => {
  if (!user) return '?';
  const f = user.first_name?.[0] || '';
  const l = user.last_name?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7)  return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

/* ─── Component ───────────────────────────────────── */
const ListenerEpisode = () => {
  const { podcastClubId, episodeId } = useParams();
  const {
    audioUrl: globalAudioUrl, isPlaying,
    setAudioUrl, setFrom, setFeaturedImage,
    setEpisodeId: setGlobalEpisodeId,
    seekTo, setPlay, setPause,
  } = useAudioPlayerStore();

  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [pivot, setPivot]               = useState({});
  const [episode, setEpisode]           = useState({});
  const [podcastClub, setPodcastClub]   = useState({});
  const [skills, setSkills]             = useState([]);
  const [keyTakeaways, setKeyTakeaways] = useState([]);
  const [discussions, setDiscussions]   = useState([]);
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [selectedPractice, setSelectedPractice]   = useState(null);
  const [selectedTakeawayId, setSelectedTakeawayId] = useState(null);
  const [selectedTakeaway, setSelectedTakeaway]   = useState(null);
  const [activeTab, setActiveTab]       = useState('takeaways');

  /* ── Practice handlers ── */
  const closePracticeModal = () => {
    setShowPracticeModal(false);
    setSelectedPractice(null);
    setSelectedTakeawayId(null);
    setSelectedTakeaway(null);
  };

  const openCreatePracticeModal = (takeaway) => {
    setSelectedTakeawayId(takeaway.id);
    setSelectedTakeaway(takeaway);
    setSelectedPractice(null);
    setShowPracticeModal(true);
  };

  const handlePracticeChange = (saved) => {
    setKeyTakeaways(prev => prev.map(t => {
      if (t.id !== selectedTakeawayId) return t;
      const exists = (t.practices || []).some(p => p.id === saved.id);
      return {
        ...t,
        practices: exists
          ? t.practices.map(p => p.id === saved.id ? saved : p)
          : [...(t.practices || []), saved],
      };
    }));
  };

  const handlePracticeDeleted = (practiceId) => {
    setKeyTakeaways(prev => prev.map(t => {
      if (t.id !== selectedTakeawayId) return t;
      return { ...t, practices: (t.practices || []).filter(p => p.id !== practiceId) };
    }));
  };

  /* ── Audio helpers ── */
  const parseTimeToSeconds = (s) => {
    if (!s) return 0;
    const parts = s.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return Number(s) || 0;
  };

  const playFromTime = (timeStr) => {
    if (!episode?.audio_url) return;
    if (globalAudioUrl !== episode.audio_url) {
      setAudioUrl(episode.audio_url);
      setFrom(episode.title || podcastClub.title || 'Now playing');
      setFeaturedImage(getImage(podcastClub, 'super_wide') || null);
      setGlobalEpisodeId(episodeId);
    } else {
      setPlay();
    }
    seekTo(parseTimeToSeconds(timeStr));
  };

  const handlePlayPause = () => {
    if (globalAudioUrl === episode.audio_url) {
      isPlaying ? setPause() : setPlay();
    } else {
      setAudioUrl(episode.audio_url);
      setFrom(episode.title || podcastClub.title || 'Now playing');
      setFeaturedImage(getImage(podcastClub, 'super_wide') || null);
      setGlobalEpisodeId(episodeId);
    }
  };

  /* ── Data loading ── */
  useEffect(() => {
    if (podcastClubId && episodeId) loadEpisode();
  }, [podcastClubId, episodeId]);

  const loadEpisode = () => {
    setError(null);
    httpService.get(`/dash/listener-podcast-club/${podcastClubId}/episode/${episodeId}`)
      .then(res => {
        const d = res.data.data;
        setPivot(d.pivot);
        setEpisode(d.episode);
        setSkills(d.skills || []);
        setKeyTakeaways(d.key_takeaways || []);
        setDiscussions(d.discussions || []);
        setPodcastClub(d.podcast_club);
      })
      .catch(err => setError(err.response?.data?.message || 'Error loading episode'))
      .finally(() => setLoading(false));
  };

  /* ── Derived ── */
  const isThisPlaying   = globalAudioUrl === episode?.audio_url && isPlaying;
  const heroImage = getImage(podcastClub, 'super_wide');
  const clubUrl   = `/listener-podcast-club/${podcastClub.guid}`;

  /* ══════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════ */
  return (
    <LoadingFade id="ListenerEpisode-component" loading={loading} stateKey={loading ? 'loading' : 'loaded'}>

      {/* ── Hero ──────────────────────────────────── */}
      <div
        className="ep-hero"
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : { background: 'linear-gradient(135deg, #113F67 0%, #5A9CB5 100%)' }}
      >
        <div className="ep-hero__scrim">

          {/* Top bar: breadcrumb + back */}
          <div className="d-flex justify-content-between align-items-start">
            <NavLink className="ep-hero__breadcrumb" to={clubUrl}>
              ← {podcastClub.title}
            </NavLink>
            <a className="ep-hero__back-btn" href={clubUrl}>
              <FaStepBackward /> Back
            </a>
          </div>

          {/* Bottom: meta + title + play */}
          <div>
            {pivot?.start_date_time && (
              <div className="ep-hero__meta">
                {new Date(pivot.start_date_time).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </div>
            )}
            <h1 className="ep-hero__title">{pivot?.title || episode?.title}</h1>
            {episode?.audio_url && (
              <button type="button" className="ep-hero__play-btn" onClick={handlePlayPause}>
                {isThisPlaying ? <><FaPause />Pause</> : <><FaPlay />Play Episode</>}
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ── Two-column content ─────────────────────── */}
      <div className="container-lg py-4 px-3 px-lg-4">
        <Row className="g-4">

          {/* Main column — tabs */}
          <Col lg={8}>
            <Tabs
              activeKey={activeTab}
              onSelect={k => setActiveTab(k)}
              id="episode-tabs"
              className="mb-3"
            >

              {/* ══ Insights tab ═══════════════════════ */}
              <Tab
                eventKey="takeaways"
                title={
                  <span>
                    <FaRegLightbulb className="me-1" />
                    Insights
                    {keyTakeaways.length > 0 && (
                      <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                        {keyTakeaways.length}
                      </Badge>
                    )}
                  </span>
                }
              >
                {keyTakeaways.length > 0 ? (
                  <div className="insight-list">
                    {keyTakeaways.map(takeaway => {
                      const practiceCount = (takeaway.practices || []).length;

                      return (
                        <div className="insight-row" key={takeaway.id}>
                          <div className="insight-row__thumb">
                            <KeyTakeawayImageWithPlaceholder image={takeaway.latest_image} />
                          </div>

                          <div className="insight-row__body">
                            {takeaway.quote && (
                              <p className="insight-row__quote">&ldquo;{takeaway.quote}&rdquo;</p>
                            )}
                            <h3 className="insight-row__title">{takeaway.title}</h3>
                            {(practiceCount > 0 || takeaway.start_time) && (
                              <p className="insight-row__meta">
                                {practiceCount > 0 && (
                                  <NavLink to="/listener-practices" className="insight-row__practices">
                                    {practiceCount} practice{practiceCount !== 1 ? 's' : ''} logged
                                  </NavLink>
                                )}
                                {practiceCount > 0 && takeaway.start_time && <span aria-hidden="true">&middot;</span>}
                                {takeaway.start_time && (
                                  <button
                                    type="button"
                                    className="insight-row__jump"
                                    title={`Jumps the episode audio to ${takeaway.start_time}, where this insight is discussed`}
                                    onClick={() => playFromTime(takeaway.start_time)}
                                  >
                                    <FaPlay aria-hidden="true" /> Jump to this moment
                                  </button>
                                )}
                              </p>
                            )}
                          </div>

                          <div className="insight-row__action">
                            <button
                              type="button"
                              className="insight-row__practice-btn"
                              onClick={() => openCreatePracticeModal(takeaway)}
                            >
                              Practice this
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <FaRegLightbulb style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                    <p className="mt-3 mb-0">No insights available yet.</p>
                  </div>
                )}
              </Tab>

              {/* ══ Public Practices tab ═══════════════ */}
              <Tab
                eventKey="public-practices"
                title={<span><FaUsers className="me-1" />Public Practices</span>}
              >
                {activeTab === 'public-practices' && (
                  <ListenerEpisodePublicPractices
                    podcastClubId={podcastClubId}
                    episodeId={episodeId}
                  />
                )}
              </Tab>

              {/* ══ Discussions tab ════════════════════ */}
              <Tab
                eventKey="discussions"
                title={
                  <span>
                    <FaComments className="me-1" />
                    Discussions
                    {discussions.length > 0 && (
                      <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                        {discussions.length}
                      </Badge>
                    )}
                  </span>
                }
              >
                <div className="mt-3 mb-5">
                  {discussions.length > 0 ? discussions.map(disc => (
                    <div key={disc.guid} className="disc-row">
                      <div className="disc-avatar">{getInitials(disc.user)}</div>
                      <div className="disc-row__body">
                        <div className="disc-row__title">
                          {disc.is_pinned === 1 && (
                            <Badge bg="warning" text="dark" className="me-1" style={{ fontSize: '0.62rem' }}>
                              📌 Pinned
                            </Badge>
                          )}
                          {disc.title}
                        </div>
                        <div className="disc-row__meta">
                          <span>
                            <FaCommentAlt className="me-1" />
                            {disc.comments_count} comment{disc.comments_count !== 1 ? 's' : ''}
                          </span>
                          {disc.last_comment_at && (
                            <span>Last activity {relativeTime(disc.last_comment_at)}</span>
                          )}
                        </div>
                      </div>
                      <div className="disc-row__action">
                        <OverlayTrigger placement="left" overlay={<Tooltip>Open discussion</Tooltip>}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            href={`/listener-podcast-club/${podcastClub.guid}/discussion/${disc.guid}`}
                          >
                            <FaExternalLinkAlt />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-5 text-muted">
                      <FaComments style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                      <p className="mt-3 mb-0">No discussions for this episode yet.</p>
                    </div>
                  )}
                </div>
              </Tab>

            </Tabs>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="ep-sidebar">

              {/* Club card */}
              <Card className="ep-sidebar__club-card">
                {heroImage && (
                  <img
                    src={heroImage}
                    alt={podcastClub.title}
                    className="ep-sidebar__club-card-img"
                  />
                )}
                <div className="ep-sidebar__club-card-body">
                  <div className="ep-sidebar__club-card-title">{podcastClub.title}</div>
                  {skills.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {skills.map(skill => (
                        <Badge
                          key={skill.id}
                          bg="light"
                          text="dark"
                          className="border"
                          style={{ fontSize: '0.68rem', fontWeight: 500 }}
                        >
                          {skill.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Discussions preview */}
              {discussions.length > 0 && (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
                  <Card.Body className="p-3">
                    <div className="ep-sidebar__section-label">Discussions</div>
                    {discussions.slice(0, 3).map(disc => (
                      <a
                        key={disc.guid}
                        href={`/listener-podcast-club/${podcastClub.guid}/discussion/${disc.guid}`}
                        className="ep-sidebar__disc-item"
                      >
                        <div className="disc-avatar disc-avatar--sm">{getInitials(disc.user)}</div>
                        <div>
                          <div className="ep-sidebar__disc-title">{disc.title}</div>
                          <div className="ep-sidebar__disc-meta">
                            <FaCommentAlt style={{ fontSize: '0.58rem', marginRight: '3px' }} />
                            {disc.comments_count} · {relativeTime(disc.last_comment_at)}
                          </div>
                        </div>
                      </a>
                    ))}
                    {discussions.length > 3 && (
                      <div className="mt-2 pt-1 text-center">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setActiveTab('discussions')}
                        >
                          +{discussions.length - 3} more discussions →
                        </button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            </div>
          </Col>

        </Row>
      </div>

      {/* Practice modal */}
      <PracticeModal
        show={showPracticeModal}
        onHide={closePracticeModal}
        takeaway={selectedTakeaway}
        practice={selectedPractice}
        keyTakeawayId={selectedTakeawayId}
        podcastClub={podcastClub}
        onPracticeChange={handlePracticeChange}
        onPracticeDeleted={handlePracticeDeleted}
      />
    </LoadingFade>
  );
};

export default ListenerEpisode;
